import React, { useState, useEffect } from 'react';
import { ChevronRight, CheckCircle2, Clock, Award, LogOut, ChevronLeft, AlertCircle } from 'lucide-react';
import { courseCurriculum } from './course-curriculum-data';

const PalladiumLMS = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('login');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [completedLessons, setCompletedLessons] = useState([]);
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const courses = courseCurriculum;

  // Load data from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('palladium-lms');
    if (saved) {
      const data = JSON.parse(saved);
      setCurrentUser(data.currentUser);
      setEnrolledCourses(data.enrolledCourses);
      setIsAdmin(data.isAdmin);
      setCompletedLessons(data.completedLessons);
    }
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('palladium-lms', JSON.stringify({
        currentUser,
        enrolledCourses,
        isAdmin,
        completedLessons
      }));
    }
  }, [currentUser, enrolledCourses, isAdmin, completedLessons]);

  const handleLogin = (username, password, admin = false) => {
    if (username && password) {
      setCurrentUser({ username, loginTime: new Date() });
      setIsAdmin(admin);
      setCurrentView(admin ? 'admin' : 'dashboard');
    }
  };

  const handleEnroll = (courseId) => {
    if (!enrolledCourses.find(c => c.id === courseId)) {
      setEnrolledCourses([...enrolledCourses, { id: courseId, enrolledAt: new Date(), progress: 0 }]);
    }
  };

  const handleStartCourse = (courseId) => {
    setCurrentCourse(courseId);
    setCurrentLesson(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setCurrentView('lesson');
  };

  const handleSubmitQuiz = () => {
    const course = courses.find(c => c.id === currentCourse);
    const lesson = course.lessons[currentLesson];
    
    // Check if all questions are answered
    if (lesson.quiz && lesson.quiz.length > 0) {
      const allAnswered = lesson.quiz.every((_, idx) => quizAnswers[`q${idx}`] !== undefined);
      if (!allAnswered) {
        alert('Please answer all questions before submitting.');
        return;
      }
    }
    
    setQuizSubmitted(true);
  };

  const handleCompleteLesson = () => {
    const lessonKey = `${currentCourse}-${currentLesson}`;
    if (!completedLessons.includes(lessonKey)) {
      setCompletedLessons([...completedLessons, lessonKey]);
    }
    
    const course = courses.find(c => c.id === currentCourse);
    if (currentLesson < course.lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
      setQuizAnswers({});
      setQuizSubmitted(false);
    } else {
      setCurrentView('certificate');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentView('login');
    setCurrentCourse(null);
    setQuizAnswers({});
  };

  const getCourseProgress = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    const completed = course.lessons.filter(lesson => 
      completedLessons.includes(`${courseId}-${course.lessons.indexOf(lesson)}`)
    ).length;
    return Math.round((completed / course.lessons.length) * 100);
  };

  const getAllEnrollments = () => {
    return enrolledCourses.map(ec => {
      const course = courses.find(c => c.id === ec.id);
      return {
        courseId: ec.id,
        courseName: course.title,
        enrolledAt: ec.enrolledAt,
        progress: getCourseProgress(ec.id)
      };
    });
  };

  // LOGIN VIEW
  if (currentView === 'login') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0c3b46 0%, #1a525e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '8px', padding: '40px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '32px', fontWeight: '600', marginBottom: '8px', color: '#0c3b46' }}>Palladium</h1>
          <p style={{ color: '#46585a', marginBottom: '30px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px' }}>FQHC Training Platform</p>
          
          <LoginForm onLogin={handleLogin} />
        </div>
      </div>
    );
  }

  // DASHBOARD VIEW
  if (currentView === 'dashboard' && currentUser) {
    return (
      <div style={{ minHeight: '100vh', background: '#f7f1e6' }}>
        <Header user={currentUser} onLogout={handleLogout} />
        
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '36px', fontWeight: '600', marginBottom: '10px', color: '#0c3b46' }}>Welcome, {currentUser.username}</h1>
          <p style={{ color: '#46585a', fontSize: '18px', marginBottom: '40px' }}>Continue your learning journey in FQHC compliance and workforce training</p>

          {enrolledCourses.length > 0 && (
            <div style={{ marginBottom: '50px' }}>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#0c3b46' }}>Your Courses</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {enrolledCourses.map(ec => {
                  const course = courses.find(c => c.id === ec.id);
                  const progress = getCourseProgress(ec.id);
                  const isComplete = progress === 100;
                  return (
                    <div key={ec.id} style={{ background: 'white', borderRadius: '6px', padding: '24px', boxShadow: '0 4px 12px rgba(12,59,70,.1)', border: '1px solid #e0d9ca', position: 'relative' }}>
                      {isComplete && <div style={{ position: 'absolute', top: '12px', right: '12px' }}><CheckCircle2 size={28} style={{ color: '#d98a28' }} /></div>}
                      <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#0c3b46' }}>{course.title}</h3>
                      <p style={{ color: '#46585a', fontSize: '13px', marginBottom: '12px' }}>{course.duration}</p>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ background: '#efe6d4', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ background: '#d98a28', height: '100%', width: `${progress}%`, transition: 'width 0.3s ease' }} />
                        </div>
                        <p style={{ fontSize: '13px', color: '#46585a', marginTop: '6px' }}>{progress}% Complete</p>
                      </div>
                      <button
                        onClick={() => handleStartCourse(ec.id)}
                        style={{ width: '100%', padding: '10px', background: isComplete ? '#ddd' : '#d98a28', color: isComplete ? '#666' : '#0c3b46', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: isComplete ? 'default' : 'pointer', fontSize: '14px' }}
                        disabled={isComplete}
                      >
                        {isComplete ? '✓ Completed' : 'Continue Course'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#0c3b46' }}>Available Courses</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
              {courses.filter(c => !enrolledCourses.find(ec => ec.id === c.id)).map(course => (
                <div key={course.id} style={{ background: 'white', borderRadius: '6px', padding: '24px', boxShadow: '0 4px 12px rgba(12,59,70,.1)', border: '1px solid #e0d9ca' }}>
                  <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#0c3b46' }}>{course.title}</h3>
                  <p style={{ color: '#46585a', fontSize: '14px', marginBottom: '14px', lineHeight: '1.6' }}>{course.description}</p>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#46585a', marginBottom: '16px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={14} /> {course.duration}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      📚 {course.lessons.length} lessons
                    </span>
                  </div>
                  <button
                    onClick={() => handleEnroll(course.id)}
                    style={{ width: '100%', padding: '10px', background: '#d98a28', color: '#0c3b46', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Enroll Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LESSON VIEW
  if (currentView === 'lesson' && currentCourse) {
    const course = courses.find(c => c.id === currentCourse);
    const lesson = course.lessons[currentLesson];
    const isLastLesson = currentLesson === course.lessons.length - 1;
    const hasQuiz = lesson.quiz && lesson.quiz.length > 0;

    return (
      <div style={{ minHeight: '100vh', background: '#f7f1e6' }}>
        <Header user={currentUser} onLogout={handleLogout} />
        
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '30px 20px' }}>
          <button
            onClick={() => setCurrentView('dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#0c3b46', cursor: 'pointer', fontSize: '14px', fontWeight: '600', marginBottom: '20px' }}
          >
            <ChevronLeft size={16} /> Back to Dashboard
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '30px' }}>
            <div>
              <div style={{ background: 'white', borderRadius: '6px', padding: '40px', boxShadow: '0 4px 12px rgba(12,59,70,.1)' }}>
                <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '32px', fontWeight: '600', marginBottom: '10px', color: '#0c3b46' }}>{lesson.title}</h1>
                <p style={{ color: '#46585a', marginBottom: '30px', fontSize: '14px' }}>{lesson.duration} • Lesson {currentLesson + 1} of {course.lessons.length}</p>

                <div style={{ color: '#1b2a2c', lineHeight: '1.8', marginBottom: '30px', whiteSpace: 'pre-wrap', fontSize: '15px', fontFamily: 'Georgia, serif' }}>
                  {lesson.content}
                </div>

                {hasQuiz && (
                  <div style={{ borderTop: '2px solid #efe6d4', paddingTop: '30px' }}>
                    <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#0c3b46' }}>Check Your Understanding</h2>
                    {lesson.quiz.map((q, idx) => {
                      const selectedAnswer = quizAnswers[`q${idx}`];
                      const isCorrect = selectedAnswer === q.correct;
                      const answered = selectedAnswer !== undefined;
                      
                      return (
                        <div key={idx} style={{ marginBottom: '24px' }}>
                          <p style={{ fontWeight: '600', marginBottom: '12px', color: '#0c3b46', fontSize: '15px' }}>
                            Question {idx + 1}: {q.question}
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {q.options.map((option, optIdx) => {
                              const isSelected = selectedAnswer === optIdx;
                              const showResult = quizSubmitted && answered;
                              let bgColor = 'transparent';
                              let textColor = '#1b2a2c';
                              
                              if (showResult) {
                                if (isSelected && isCorrect) {
                                  bgColor = '#c6e5a8';
                                  textColor = '#2d5016';
                                } else if (isSelected && !isCorrect) {
                                  bgColor = '#f5a5a5';
                                  textColor = '#8b0000';
                                } else if (optIdx === q.correct) {
                                  bgColor = '#c6e5a8';
                                  textColor = '#2d5016';
                                }
                              } else if (isSelected) {
                                bgColor = '#d98a28';
                                textColor = 'white';
                              }
                              
                              return (
                                <label key={optIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px', borderRadius: '4px', background: bgColor, color: textColor, border: '1px solid #ddd' }}>
                                  <input
                                    type="radio"
                                    name={`question${idx}`}
                                    checked={isSelected}
                                    onChange={() => !quizSubmitted && setQuizAnswers({ ...quizAnswers, [`q${idx}`]: optIdx })}
                                    disabled={quizSubmitted}
                                    style={{ cursor: quizSubmitted ? 'not-allowed' : 'pointer' }}
                                  />
                                  {option}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '30px', borderTop: '2px solid #efe6d4', paddingTop: '30px' }}>
                  <button
                    onClick={() => currentLesson > 0 && setCurrentLesson(currentLesson - 1)}
                    disabled={currentLesson === 0}
                    style={{ padding: '10px 20px', background: currentLesson === 0 ? '#e0e0e0' : 'white', color: currentLesson === 0 ? '#ccc' : '#0c3b46', border: '1px solid #d0c8ba', borderRadius: '4px', cursor: currentLesson === 0 ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                  >
                    Previous Lesson
                  </button>
                  
                  {!quizSubmitted && hasQuiz && (
                    <button
                      onClick={handleSubmitQuiz}
                      style={{ flex: 1, padding: '10px 20px', background: '#d98a28', color: '#0c3b46', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Submit Quiz
                    </button>
                  )}
                  
                  {(!hasQuiz || quizSubmitted) && (
                    <button
                      onClick={handleCompleteLesson}
                      style={{ flex: 1, padding: '10px 20px', background: '#d98a28', color: '#0c3b46', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      {isLastLesson ? 'Complete Course' : 'Next Lesson'} →
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div style={{ background: 'white', borderRadius: '6px', padding: '20px', boxShadow: '0 4px 12px rgba(12,59,70,.1)', position: 'sticky', top: '100px' }}>
                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#0c3b46' }}>Course Progress</h3>
                <div style={{ background: '#efe6d4', height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
                  <div style={{ background: '#d98a28', height: '100%', width: `${((currentLesson + 1) / course.lessons.length) * 100}%` }} />
                </div>
                <p style={{ fontSize: '13px', color: '#46585a', marginBottom: '20px' }}>{currentLesson + 1} of {course.lessons.length}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {course.lessons.map((l, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px' }}>
                      {completedLessons.includes(`${currentCourse}-${idx}`) ? (
                        <CheckCircle2 size={16} style={{ color: '#d98a28', flexShrink: 0, marginTop: '2px' }} />
                      ) : (
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid #d0c8ba', flexShrink: 0, marginTop: '2px' }} />
                      )}
                      <span style={{ color: idx === currentLesson ? '#0c3b46' : '#46585a', fontWeight: idx === currentLesson ? '600' : '400' }}>
                        {l.title.split(' ').slice(0, 3).join(' ')}...
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CERTIFICATE VIEW
  if (currentView === 'certificate' && currentCourse) {
    const course = courses.find(c => c.id === currentCourse);
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0c3b46 0%, #1a525e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '8px', padding: '60px', maxWidth: '600px', width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <Award size={60} style={{ margin: '0 auto 20px', color: '#d98a28' }} />
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '36px', fontWeight: '600', marginBottom: '10px', color: '#0c3b46' }}>Congratulations!</h1>
          <p style={{ color: '#46585a', fontSize: '18px', marginBottom: '30px' }}>You have successfully completed</p>
          
          <div style={{ background: '#efe6d4', padding: '30px', borderRadius: '6px', marginBottom: '30px' }}>
            <p style={{ fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: '600', color: '#0c3b46', margin: '0' }}>{course.title}</p>
            <p style={{ color: '#46585a', fontSize: '14px', marginTop: '10px' }}>Certificate of Completion</p>
          </div>

          <p style={{ color: '#46585a', fontSize: '14px', marginBottom: '30px' }}>
            Completion Date: {new Date().toLocaleDateString()}<br />
            Certificate ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}
          </p>

          <p style={{ color: '#46585a', fontSize: '13px', marginBottom: '30px', lineHeight: '1.6', maxWidth: '500px' }}>
            This certificate verifies that you have completed the required training in {course.title} as mandated by HRSA and your Federally Qualified Health Center. This certificate should be kept in your personnel file for compliance documentation.
          </p>

          <button
            onClick={() => setCurrentView('dashboard')}
            style={{ padding: '12px 30px', background: '#d98a28', color: '#0c3b46', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', fontSize: '16px' }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ADMIN VIEW
  if (currentView === 'admin' && isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: '#f7f1e6' }}>
        <Header user={currentUser} onLogout={handleLogout} />
        
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '36px', fontWeight: '600', marginBottom: '30px', color: '#0c3b46' }}>Admin Dashboard</h1>

          <div style={{ background: 'white', borderRadius: '6px', padding: '30px', boxShadow: '0 4px 12px rgba(12,59,70,.1)', marginBottom: '30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#0c3b46' }}>Training Overview</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: '#f0f8ff', padding: '20px', borderRadius: '6px', borderLeft: '4px solid #d98a28' }}>
                <p style={{ fontSize: '12px', color: '#46585a', textTransform: 'uppercase', fontWeight: '600' }}>Total Courses</p>
                <p style={{ fontSize: '28px', color: '#0c3b46', fontWeight: '600', margin: '8px 0 0 0' }}>{courses.length}</p>
              </div>
              <div style={{ background: '#f0f8ff', padding: '20px', borderRadius: '6px', borderLeft: '4px solid #d98a28' }}>
                <p style={{ fontSize: '12px', color: '#46585a', textTransform: 'uppercase', fontWeight: '600' }}>Total Enrollments</p>
                <p style={{ fontSize: '28px', color: '#0c3b46', fontWeight: '600', margin: '8px 0 0 0' }}>{enrolledCourses.length}</p>
              </div>
              <div style={{ background: '#f0f8ff', padding: '20px', borderRadius: '6px', borderLeft: '4px solid #d98a28' }}>
                <p style={{ fontSize: '12px', color: '#46585a', textTransform: 'uppercase', fontWeight: '600' }}>Completions</p>
                <p style={{ fontSize: '28px', color: '#0c3b46', fontWeight: '600', margin: '8px 0 0 0' }}>{enrolledCourses.filter(ec => getCourseProgress(ec.id) === 100).length}</p>
              </div>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '6px', padding: '30px', boxShadow: '0 4px 12px rgba(12,59,70,.1)' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#0c3b46' }}>Enrollment & Completion Tracking</h2>
            
            {getAllEnrollments().length === 0 ? (
              <p style={{ color: '#46585a' }}>No enrollments yet. Once users enroll in courses, their progress will appear here.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #efe6d4' }}>
                    <th style={{ textAlign: 'left', padding: '12px', color: '#0c3b46', fontWeight: '600' }}>Course</th>
                    <th style={{ textAlign: 'left', padding: '12px', color: '#0c3b46', fontWeight: '600' }}>Enrolled</th>
                    <th style={{ textAlign: 'left', padding: '12px', color: '#0c3b46', fontWeight: '600' }}>Progress</th>
                    <th style={{ textAlign: 'left', padding: '12px', color: '#0c3b46', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {getAllEnrollments().map((enrollment, idx) => {
                    const isComplete = enrollment.progress === 100;
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #e0d9ca' }}>
                        <td style={{ padding: '12px', color: '#1b2a2c' }}>{enrollment.courseName}</td>
                        <td style={{ padding: '12px', color: '#46585a', fontSize: '14px' }}>{new Date(enrollment.enrolledAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ background: '#efe6d4', height: '8px', borderRadius: '4px', overflow: 'hidden', width: '120px' }}>
                            <div style={{ background: '#d98a28', height: '100%', width: `${enrollment.progress}%` }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#46585a' }}>{enrollment.progress}%</span>
                        </td>
                        <td style={{ padding: '12px', color: isComplete ? '#4CAF50' : '#FF9800', fontWeight: '600' }}>
                          {isComplete ? '✓ Completed' : 'In Progress'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// LOGIN FORM COMPONENT
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e, isAdmin = false) => {
    e.preventDefault();
    onLogin(username, password, isAdmin);
  };

  return (
    <form onSubmit={(e) => handleSubmit(e, false)}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#0c3b46', fontSize: '14px' }}>Username</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter your username"
          style={{ width: '100%', padding: '10px', border: '1px solid #d0c8ba', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit' }}
        />
      </div>
      
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#0c3b46', fontSize: '14px' }}>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          style={{ width: '100%', padding: '10px', border: '1px solid #d0c8ba', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit' }}
        />
      </div>

      <button
        type="submit"
        style={{ width: '100%', padding: '12px', background: '#d98a28', color: '#0c3b46', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', fontSize: '16px', marginBottom: '12px' }}
      >
        Login
      </button>

      <button
        onClick={(e) => handleSubmit(e, true)}
        style={{ width: '100%', padding: '12px', background: '#0c3b46', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer', fontSize: '16px' }}
      >
        Admin Login
      </button>

      <p style={{ fontSize: '12px', color: '#46585a', marginTop: '16px', textAlign: 'center' }}>Demo: Any username/password works</p>
    </form>
  );
};

// HEADER COMPONENT
const Header = ({ user, onLogout }) => (
  <header style={{ background: 'white', borderBottom: '1px solid #e0d9ca', padding: '16px 0', position: 'sticky', top: 0, zIndex: 40 }}>
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '3px', background: '#0c3b46', display: 'grid', placeItems: 'center', color: 'white', fontFamily: 'Georgia, serif', fontWeight: '700', fontSize: '18px' }}>P</div>
        <div>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: '600', color: '#0c3b46', margin: '0' }}>Palladium</p>
          <p style={{ fontSize: '11px', color: '#46585a', textTransform: 'uppercase', letterSpacing: '1px', margin: '0' }}>FQHC Training</p>
        </div>
      </div>

      <button
        onClick={onLogout}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#0c3b46', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
      >
        <LogOut size={16} /> Logout
      </button>
    </div>
  </header>
);

export default PalladiumLMS;
